import React, { useState, useEffect, useRef, ReactElement, useCallback, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getShadowTheme, getBackgroundTheme } from '../../../style';
import HeaderLogo from '../../../atoms/HeaderLogo';
import Container from '../Container';
import HeaderNavLink from './HeaderNavLink';
import { navbarIcons } from '../../../atoms/icons/materialIcons';
import styled from '@emotion/styled';
import { removeBodyScroll, triggerTracking } from '../../../utils/helpers';
import queryString from 'query-string';
import { useStateCallback } from '../../../utils/customHooks';
import {
  sendDashboardNavbarSearch,
  sendDashboardNavbarInterests,
  sendDashboardNavbarOffers,
  sendDashboardNavbarFavourites,
  sendDashboardNavbarMessages,
} from '../../../actions/dashboardTrackAction';
import useMixPanel from '../../../hooks/useMixPanel';
import useOnClickCallback from '../../../hooks/useOnClickCallback';
import breakpoints from '../../../theme/material/breakpoints';
import { DesktopWrapper, MobileWrapper } from '../../common/appLayout/StyledElements';
import SliderSidebar from './Sidebar/SliderSidebar';
import { EVENT_NAMES } from '../../../constants/mixpanel';

const navBarHeight = 65;

const containerStyles = {
  display: 'flex',
  [breakpoints.down(639.05)]: { justifyContent: 'space-between' },
};

const SidebarButton = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 8,
  flexBasis: '100%',
  alignItems: 'center',
});

const linkStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  flexBasis: '100%',
  alignItems: 'center',
};

const WYPTextContainer = styled('div')({
  paddingTop: 8,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const determinePosition = (positionAbsolute: boolean) => {
  return positionAbsolute ? 'absolute' : 'fixed';
};

// changed from 1383d2 to FFF, icons to change to #E2C92D
const SiteHeader = styled('div')<{ activeSiteNotification: boolean; isAbsolutePos: boolean }>(
  ({ activeSiteNotification, isAbsolutePos }) => ({
    boxShadow: getShadowTheme().header,
    backgroundColor: getBackgroundTheme().block,
    verticalAlign: 'middle',
    position: activeSiteNotification ? 'relative' : determinePosition(isAbsolutePos),
    top: 0,
    left: 0,
    right: 0,
    height: 'auto',
    [breakpoints.down('sm')]: {
      height: 48,
    },
    zIndex: 997,
  })
);

const NavBar = styled('div')<{ activeSiteNotification: boolean }>(({ activeSiteNotification }) => ({
  paddingTop: activeSiteNotification ? '0px' : `${navBarHeight}px`,
  [breakpoints.down('sm')]: {
    paddingBottom: activeSiteNotification ? 16 : 0,
  },
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const LogoWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingLeft: 15,
  paddingRight: 16,
  [breakpoints.down('sm')]: {
    paddingRight: 0,
  },
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const DesktopHeaderNavContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  [breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileHeaderNavContainer = styled('div')(() => ({
  display: 'none',
  [breakpoints.down('sm')]: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
}));

const renderIcon = (
  isDarkMode: boolean,
  icon: { dark: () => void; default: () => void; darkActive: () => void; active: () => void },
  status: string
) => {
  switch (status) {
    case 'default':
      return isDarkMode ? icon.dark : icon.default;
    case 'active':
      return isDarkMode ? icon.darkActive : icon.active;
    default:
      return;
  }
};

const renderLogo = () => {
  return <HeaderLogo width={180} height={23} />;
};

const setMenuCallback = (isShow: boolean, location, navigate) => {
  if (!isShow && location && location.search) {
    const queryStringParsed = queryString.parse(location.search);
    const showSettings = queryStringParsed.showSettings === 'true';

    if (showSettings) {
      navigate(location.pathname);
    }
  }
};

const setCorrectPageAction = (_e: Event, page: string) => {
  switch (page) {
    case 'Search':
      sendDashboardNavbarSearch();
      break;
    case 'Favourites':
      sendDashboardNavbarFavourites();
      break;
    case 'Interests':
      sendDashboardNavbarInterests();
      break;
    case 'Offers':
      sendDashboardNavbarOffers();
      break;
    case 'Messages':
      sendDashboardNavbarMessages();
      break;
    default:
      return;
  }
};

interface HeaderProps {
  location?: any;
  toggleSidebar: any;
  history?: any;
  hideSidebar: any;
  username: any;
  newFavorites: any;
  newOffers: any;
  messages: any;
  avatar: any;
  canUpgrade: any;
  credits: any;
  nagNotification: any;
  pathname: any;
  hashId: any;
  hasGlobalNotif: any;
  isMobile: any;
  isDarkMode: any;
  requiredVerification: any;
  showPaymentSettings: any;
}

const Header = ({
  toggleSidebar,
  history,
  hideSidebar,
  username,
  newFavorites,
  newOffers,
  messages,
  avatar,
  canUpgrade,
  credits,
  nagNotification,
  pathname,
  hashId,
  hasGlobalNotif,
  isMobile,
  isDarkMode,
  requiredVerification,
  showPaymentSettings,
  ...rest
}: HeaderProps): ReactElement | null => {
  const [menuOpen, setMenuOpen] = useStateCallback(false);
  const [hasNotify, setHasNotify] = useState(false);
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const visitSearch = useRef(null);
  const visitFavourites = useRef(null);
  const visitOffers = useRef(null);
  const visitMessages = useRef(null);
  const { trackPageView, setMixpanelDataByKeyHandler } = useMixPanel();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    triggerTracking('Search', visitSearch, setCorrectPageAction);
    triggerTracking('Interests', visitFavourites, setCorrectPageAction);
    triggerTracking('Offers', visitOffers, setCorrectPageAction);
    triggerTracking('Messages', visitMessages, setCorrectPageAction);
  }, []);

  useOnClickCallback(visitSearch, () => {
    setMixpanelDataByKeyHandler({
      key: EVENT_NAMES.SEARCH_VIEW,
      payload: { Source: 'Menu' },
    });
  });

  //   you can also use this function on any of your event to open/close the sidebar
  const handleSidebarToggle = useCallback(
    (isShow: boolean, notified = false) => {
      removeBodyScroll();
      toggleSidebar();
      setHasNotify(notified);
      setMenuOpen(isShow, newState => setMenuCallback(newState, location, navigate));

      if (isShow) {
        trackPageView('menu');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMenuOpen, toggleSidebar]
  );

  useEffect(() => {
    if (location && location.search) {
      const queryStringParsed = queryString.parse(location.search);
      const showSettings = queryStringParsed.showSettings === 'true';
      const showInfo = queryStringParsed.accountInfo === 'true';

      if (showSettings) {
        setShowAccountInfo(showInfo);
        handleSidebarToggle(true);
      }
    } else {
      setShowAccountInfo(false);
    }
  }, []);

  const handleSidebarHide = (_isShow: boolean, notified = false) => {
    removeBodyScroll();
    hideSidebar();
    setMenuOpen(false);
    setHasNotify(notified);
  };

  const noNavPages =
    isMobile &&
    (pathname.indexOf('/payment') > -1 ||
      pathname.indexOf('/packages') > -1 ||
      pathname.indexOf('/billing') > -1 ||
      pathname.split('/')[1] === 'profile');

  const isSearchPage = pathname.indexOf('/search') > -1;
  const hideBuyCredits =
    pathname.indexOf('/payment') > -1 ||
    pathname.indexOf('/packages') > -1 ||
    pathname.indexOf('/billing') > -1 ||
    !canUpgrade;

  // add hiding footer nave when user is in mobile and profile page
  if (noNavPages && pathname.split('/')[1] === 'profile') return null;

  return (
    <NavBar activeSiteNotification={hasGlobalNotif}>
      <SiteHeader
        id="site-header"
        activeSiteNotification={hasGlobalNotif}
        isAbsolutePos={isSearchPage && isMobile}
        style={{ background: getBackgroundTheme().headerCTA }}
      >
        <Container
          data-test-id="header-inner-links"
          wideHeader
          fullWidth
          css={{
            ...containerStyles,
            ...{ maxHeight: 'auto' },
            ...(isMobile ? { padding: 0 } : {}),
          }}
        >
          <LogoWrapper
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            marginRight={12}
          >
            <MobileWrapper>
              <SidebarButton data-test-id="header-avatar-button" onClick={() => handleSidebarToggle(true)}>
                <HeaderLogo avatar={avatar} iconOnly />
              </SidebarButton>
            </MobileWrapper>
            <DesktopWrapper>
              <Link
                to="/dashboard"
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                style={linkStyles}
              >
                <HeaderLogo width={280} height={35} />
              </Link>
            </DesktopWrapper>
          </LogoWrapper>

          <DesktopHeaderNavContainer id="header-nav-container">
            <HeaderNavLink
              headerRef={visitSearch}
              to="/search"
              currentPath={pathname}
              activePathPattern={/.*search/}
              iconComponent={renderIcon(isDarkMode, navbarIcons.search, 'default')}
              activeIconComponent={renderIcon(isDarkMode, navbarIcons.search, 'active')}
            />
            <HeaderNavLink
              headerRef={visitFavourites}
              to={'/interests'}
              currentPath={pathname}
              activePathPattern={/.*interests/}
              iconComponent={renderIcon(isDarkMode, navbarIcons.interests, 'default')}
              activeIconComponent={renderIcon(isDarkMode, navbarIcons.interests, 'active')}
              notification={newFavorites}
            />
            <HeaderNavLink
              headerRef={visitOffers}
              to="/offers/new"
              currentPath={pathname}
              activePathPattern={/^.offers/}
              iconComponent={renderIcon(isDarkMode, navbarIcons.offers, 'default')}
              activeIconComponent={renderIcon(isDarkMode, navbarIcons.offers, 'active')}
              notification={newOffers}
            />
            <HeaderNavLink
              headerRef={visitMessages}
              to="/mail"
              currentPath={pathname}
              activePathPattern={/^.mail\/?.*/}
              iconComponent={renderIcon(isDarkMode, navbarIcons.mail, 'default')}
              activeIconComponent={renderIcon(isDarkMode, navbarIcons.mail, 'active')}
              notification={messages}
            />
          </DesktopHeaderNavContainer>
          <MobileHeaderNavContainer style={{ marginRight: hideBuyCredits ? 15 : 0 }}>
            <Link
              to="/dashboard"
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              style={{ ...linkStyles, ...{ alignItems: hideBuyCredits ? 'flex-end' : 'center' } }}
            >
              <WYPTextContainer style={{ justifyContent: hideBuyCredits ? 'flex-end' : 'center' }}>
                {renderLogo()}
              </WYPTextContainer>
            </Link>
          </MobileHeaderNavContainer>

          <SliderSidebar
            avatar={avatar}
            credits={credits}
            username={username}
            canUpgrade={canUpgrade}
            hashId={hashId}
            handleSidebarToggle={handleSidebarToggle}
            handleSidebarHide={handleSidebarHide}
            menuOpen={menuOpen}
            hasNotify={hasNotify}
            navBarHeight={navBarHeight}
            isMobile={isMobile}
            showAccountInfo={showAccountInfo}
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            history={history}
            requiredVerification={requiredVerification}
            showPaymentSettings={showPaymentSettings}
            {...rest}
          />
        </Container>
      </SiteHeader>
      {nagNotification}
    </NavBar>
  );
};

export default memo(Header);
